import { useState } from "react";
import { Link } from "react-router-dom";
import {
  importBatch,
  importPrepare,
  resetImportedData,
  type ImportClaimInput,
  type ImportPrepareResult,
} from "../actions";
import { IcChevron, IcSeed } from "../ui/icons";

/**
 * The one-off historical import.
 *
 * The file is parsed **in the browser** rather than uploaded. It is 11.5MB of
 * real community data, and the browser already has it — sending it to storage
 * first would put a copy of everybody's personal details somewhere else for no
 * benefit. Only the records being written travel, in batches.
 *
 * ## The dry run is not optional in spirit
 *
 * A first pass over 3,893 people, 3,010 households and 722 donations should be
 * inspectable before it writes. So the dry run is the prominent action, it runs
 * the same code path the real import uses, and the counts it reports are the
 * ones that will actually happen.
 *
 * ## Why the reset is a separate, deliberately awkward step
 *
 * Importing into a database that still holds demo records and Google triage
 * cards mixes real data with test data, and afterwards nothing can tell them
 * apart. So the reset exists — but it is the single most destructive action in
 * the system, so it needs a typed confirmation and it reports exactly what it
 * removed rather than saying "done".
 */

const BATCH = 200;

type Phase = "idle" | "reading" | "prepared" | "importing" | "done";

interface Parsed {
  people: Record<string, unknown>[];
  households: Record<string, unknown>[];
  engagements: Record<string, unknown>[];
  donations: Record<string, unknown>[];
}

/** Mirrors the server's normalization closely enough to build claim inputs. */
function normalizePhone(raw: string): string {
  const digits = raw.replace(/[^\d]/g, "");
  if (raw.trim().startsWith("+")) return `+${digits}`;
  if (digits.startsWith("00")) return `+${digits.slice(2)}`;
  if (digits.startsWith("0")) return `+972${digits.slice(1)}`;
  if (digits.startsWith("972")) return `+${digits}`;
  return `+${digits}`;
}

function claimInputsFrom(people: Record<string, unknown>[]): ImportClaimInput[] {
  return people.map((p) => {
    const phones = [
      ...new Set(
        ((p.phones as { e164?: string; local?: string }[]) ?? [])
          .map((ph) => ph.e164 ?? ph.local)
          .filter((x): x is string => typeof x === "string" && x.trim() !== "")
          .map(normalizePhone)
          .filter((x) => /^\+\d{7,15}$/.test(x)),
      ),
    ];
    const prov = p.provenance as { records_merged?: number } | undefined;
    return {
      personId: String(p.person_id ?? ""),
      householdRole: (p.household_role as string | null) ?? null,
      isChild: p.is_child === true,
      phones,
      recordsMerged: typeof prov?.records_merged === "number" ? prov.records_merged : 1,
      hasEmail: Array.isArray(p.emails) && p.emails.length > 0,
    };
  });
}

function labelsFrom(parsed: Parsed): string[] {
  const out: string[] = [];
  for (const p of parsed.people) out.push(...((p.tags as string[]) ?? []));
  for (const h of parsed.households) out.push(...((h.tags as string[]) ?? []));
  return out;
}

export function ImportPage() {
  const [phase, setPhase] = useState<Phase>("idle");
  const [parsed, setParsed] = useState<Parsed | null>(null);
  const [prepared, setPrepared] = useState<ImportPrepareResult | null>(null);
  const [progress, setProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [written, setWritten] = useState<Record<string, number>>({});
  const [resetInfo, setResetInfo] = useState<string | null>(null);
  const [confirmText, setConfirmText] = useState("");

  function readable(err: unknown): string {
    const m = (err as { message?: string })?.message;
    return m && /[֐-׿]/.test(m) ? m : "משהו השתבש. נסו שוב.";
  }

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    setPhase("reading");
    setError(null);
    setPrepared(null);
    try {
      const text = await file.text();
      const json = JSON.parse(text) as Record<string, unknown>;
      // Accepts either crm_full.json or a bare array of people.
      const next: Parsed = {
        people: (json.people as Record<string, unknown>[]) ?? (Array.isArray(json) ? json : []),
        households: (json.households as Record<string, unknown>[]) ?? [],
        engagements: (json.engagements as Record<string, unknown>[]) ?? [],
        donations: (json.donations as Record<string, unknown>[]) ?? [],
      };
      if (next.people.length === 0) throw new Error("לא מצאנו אנשי קשר בקובץ.");
      setParsed(next);
      setPhase("idle");
    } catch (err) {
      setError(readable(err));
      setPhase("idle");
    }
  }

  async function prepare(dryRun: boolean) {
    if (!parsed) return;
    setError(null);
    setProgress(dryRun ? "בודקים…" : "מכינים…");
    try {
      const r = await importPrepare({
        dryRun,
        claimInputs: claimInputsFrom(parsed.people),
        labels: labelsFrom(parsed),
        counts: {
          people: parsed.people.length,
          households: parsed.households.length,
          engagements: parsed.engagements.length,
          donations: parsed.donations.length,
        },
      });
      setPrepared(r);
      setPhase(dryRun ? "idle" : "prepared");
    } catch (err) {
      setError(readable(err));
    } finally {
      setProgress(null);
    }
  }

  async function runImport() {
    if (!parsed || !prepared || prepared.dryRun) return;
    setPhase("importing");
    setError(null);
    const tally: Record<string, number> = {};

    try {
      // People first, then households, then the things that reference people.
      const order: (keyof Parsed)[] = ["people", "households", "engagements", "donations"];
      for (const kind of order) {
        const records = parsed[kind];
        for (let i = 0; i < records.length; i += BATCH) {
          const slice = records.slice(i, i + BATCH);
          setProgress(`${kind}: ${i + slice.length} מתוך ${records.length}`);
          const r = await importBatch(prepared.runId, kind, slice);
          tally[kind] = (tally[kind] ?? 0) + r.written;
          setWritten({ ...tally });
        }
      }
      setPhase("done");
    } catch (err) {
      setError(readable(err));
      setPhase("prepared");
    } finally {
      setProgress(null);
    }
  }

  async function doReset() {
    setError(null);
    setResetInfo(null);
    try {
      const r = await resetImportedData();
      setResetInfo(
        `מחקנו ${r.total} פריטים: ` +
          Object.entries(r.deletedByCollection)
            .filter(([, n]) => n > 0)
            .map(([c, n]) => `${c} ${n}`)
            .join(" · "),
      );
      setConfirmText("");
    } catch (err) {
      setError(readable(err));
    }
  }

  return (
    <div className="screen">
      <header className="hdr">
        <div className="hdr-top">
          <div className="hdr-titles">
            <h1>
              <IcSeed size={20} /> ייבוא היסטורי
            </h1>
            <p className="sub">פעולה חד־פעמית: קוראים את הקובץ, בודקים, ורק אז שומרים</p>
          </div>
        </div>
      </header>

      <div className="body">
        <Link to="/more" className="back-link">
          <IcChevron size={15} /> חזרה
        </Link>

        {error && <p className="form-error">{error}</p>}

        {/* Step 1 — the reset, first because importing on top of demo data
            cannot be undone by importing again. */}
        <section className="section">
          <div className="card">
            <div className="card-title">1 · מחיקת הנתונים הקיימים</div>
            <div className="card-pad">
              <p className="muted">
                נמחקים אנשי קשר, משפחות, קשרים, תרומות, אזכרות והאינבוקס. <strong>לא</strong> נמחקים
                המשתמשים, יומן הביקורת והנתונים הגולמיים שהגיעו מהאתר.
              </p>
              <p className="faint" style={{ marginTop: 8 }}>
                למה זה חשוב: ייבוא על מערכת שיש בה נתוני דמה מערבב אמיתי עם מדומה, ואחר כך אי אפשר
                להפריד ביניהם.
              </p>

              {resetInfo && <p className="conn-note">{resetInfo}</p>}

              <label className="form-field" style={{ padding: "12px 0 0" }}>
                <span className="form-label">
                  כדי להפעיל, הקלידו <code>מוחק הכל</code>
                </span>
                <input
                  className="form-input"
                  value={confirmText}
                  onChange={(ev) => setConfirmText(ev.target.value)}
                  placeholder="מוחק הכל"
                />
              </label>
              <button
                className="btn btn-wide"
                style={{ marginTop: 4, borderColor: "var(--red)", color: "var(--red)" }}
                disabled={confirmText.trim() !== "מוחק הכל"}
                onClick={doReset}
              >
                מחיקת הנתונים הקיימים
              </button>
            </div>
          </div>
        </section>

        {/* Step 2 — the file. */}
        <section className="section">
          <div className="card">
            <div className="card-title">2 · הקובץ</div>
            <div className="card-pad">
              <p className="muted">
                בחרו את <code>crm_full.json</code>. הקובץ נקרא כאן בדפדפן ולא עולה לשום מקום.
                נשלחים רק הנתונים עצמם, בחלקים.
              </p>
              <input
                type="file"
                accept=".json,application/json"
                onChange={onFile}
                className="form-input"
                style={{ marginTop: 10 }}
              />
              {phase === "reading" && <p className="faint">קוראים…</p>}
              {parsed && (
                <div className="conn-meta">
                  <div>
                    <span className="faint">אנשי קשר</span>
                    <strong>{parsed.people.length.toLocaleString("he-IL")}</strong>
                  </div>
                  <div>
                    <span className="faint">משפחות</span>
                    <strong>{parsed.households.length.toLocaleString("he-IL")}</strong>
                  </div>
                  <div>
                    <span className="faint">אירועים</span>
                    <strong>{parsed.engagements.length.toLocaleString("he-IL")}</strong>
                  </div>
                  <div>
                    <span className="faint">תרומות</span>
                    <strong>{parsed.donations.length.toLocaleString("he-IL")}</strong>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Step 3 — dry run, then write. */}
        {parsed && (
          <section className="section">
            <div className="card">
              <div className="card-title">3 · בדיקה וייבוא</div>
              <div className="card-pad">
                {prepared && <PrepareReport r={prepared} />}
                {progress && <p className="conn-note">{progress}</p>}

                {phase === "done" && (
                  <p className="conn-note">
                    הייבוא הושלם:{" "}
                    {Object.entries(written)
                      .map(([k, n]) => `${k} ${n}`)
                      .join(" · ")}
                  </p>
                )}

                <div className="btn-row" style={{ marginTop: 12 }}>
                  <button
                    className="btn"
                    onClick={() => prepare(true)}
                    disabled={progress !== null || phase === "importing"}
                  >
                    בדיקה בלבד
                  </button>
                  {prepared && !prepared.dryRun ? (
                    <button
                      className="btn btn-primary"
                      onClick={runImport}
                      disabled={phase === "importing" || phase === "done"}
                    >
                      {phase === "importing" ? "מייבאים…" : "ייבוא עכשיו"}
                    </button>
                  ) : (
                    <button
                      className="btn btn-primary"
                      onClick={() => prepare(false)}
                      disabled={progress !== null}
                    >
                      הכנה לייבוא
                    </button>
                  )}
                </div>
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function PrepareReport({ r }: { r: ImportPrepareResult }) {
  return (
    <div className="kb-result">
      <p className="kb-result-head">
        {r.dryRun ? "בדיקה בלבד. לא שמרנו כלום." : "מוכן לייבוא"}
      </p>

      <div className="kb-block">
        <p className="kb-block-title">זהות</p>
        <div className="conn-meta" style={{ marginTop: 0, paddingTop: 0, border: 0 }}>
          <div>
            <span className="faint">אנשי קשר</span>
            <strong>{r.people.toLocaleString("he-IL")}</strong>
          </div>
          <div>
            <span className="faint">טלפונים ייחודיים</span>
            <strong>{r.distinctPhones.toLocaleString("he-IL")}</strong>
          </div>
          <div>
            <span className="faint">טלפונים משותפים</span>
            <strong>{r.sharedPhones.toLocaleString("he-IL")}</strong>
          </div>
        </div>
        <p className="faint" style={{ marginTop: 8 }}>
          {r.peopleHoldingUnclaimedPhone.toLocaleString("he-IL")} אנשי קשר רשומים עם מספר שלא שלהם.
          ברוב המקרים זה ילד עם הטלפון של ההורה. הם ייווצרו, אבל לא נזהה אותם לפי המספר הזה.
        </p>
        <p className="faint">
          {r.peopleWithNoIdentifier.toLocaleString("he-IL")} בלי פרט מזהה משלהם. כולם מקושרים למשפחה.
        </p>
      </div>

      <div className="kb-block">
        <p className="kb-block-title">רשימות</p>
        <div className="conn-meta" style={{ marginTop: 0, paddingTop: 0, border: 0 }}>
          <div>
            <span className="faint">תגיות</span>
            <strong>{r.vocabulary.personTags}</strong>
          </div>
          <div>
            <span className="faint">אירועים</span>
            <strong>{r.vocabulary.events}</strong>
          </div>
          <div>
            <span className="faint">תחומי עסקים</span>
            <strong>{r.vocabulary.businessServices}</strong>
          </div>
        </div>
        {r.fragments.length > 0 && (
          <p className="faint" style={{ marginTop: 8 }}>
            {r.fragments.length} תגיות לא תקינות לא ייווצרו: {r.fragments.join(" · ")}
          </p>
        )}
      </div>
    </div>
  );
}
